import { useEffect, useState } from 'react';

import { css } from '@emotion/react';
import {
  MenuItem,
  MenuList,
  Popper,
  styled,
  useAutocomplete,
  useForkRef,
  useTheme,
} from '@mui/material';
// eslint-disable-next-line @nx/enforce-module-boundaries -- Module Federation singleton 키를 유지하려면 패키지 경로 import가 필요합니다.
import { usePopupContext } from '@kc-console/common-ui/singleton';

import { observeResizeEvent, useCascadeRef, useCascadeState } from '../../utils';
import { MenuItemContent } from '../MenuItemContent';
import TextHighlights from '../Select/TextHighlights';
import { TextInput } from '../TextInput';
import { comboBoxClasses } from './theme';

import type { AutocompleteInputChangeReason } from '@mui/material';
import type { ComboBoxOwnerState, ComboBoxProps } from './types';

const COMBO_BOX_VIEWPORT_MARGIN = 16;
const COMBO_BOX_GNB_OFFSET = 88;
const COMBO_BOX_MAX_HEIGHT = 300;

const ComboBox = (props: ComboBoxProps) => {
  const {
    autoWidth = 'outside',
    disabled,
    getOptionDisabled,
    filterOptions,
    inputRef: inputRefProp,
    onChange: onChangeProp,
    onClose: onCloseProp,
    onKeyDown,
    onOpen: onOpenProp,
    open: openProp,
    options,
    renderOptionSuffix,
    slotProps,
    value: valueProp,
    ref,
    ...others
  } = props;
  const { root: rootSlotProps, input: inputSlotProps, popper: popperSlotProps } = slotProps ?? {};
  const {
    modifiers: popperModifiers,
    style: popperStyle,
    ...otherPopperSlotProps
  } = popperSlotProps ?? {};
  const theme = useTheme();
  const popupContext = usePopupContext();
  const popperZIndex = popupContext?.zIndex ?? theme.zIndex.contentPopup;

  const rootRef = useCascadeRef<HTMLDivElement | null>(null, ref);
  const inputRef = useCascadeRef<HTMLInputElement | null>(null, inputRefProp);

  const [open, setOpen] = useCascadeState({
    initialValue: false,
    value: openProp,
    onChange: (value: boolean) => (value ? onOpenProp?.() : onCloseProp?.()),
  });

  const [value, onChange] = useCascadeState({
    initialValue: '',
    value: valueProp,
    onChange: (value: string, event: unknown, reason: AutocompleteInputChangeReason) =>
      onChangeProp?.(event, value, reason),
  });

  const {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    popupOpen,
    anchorEl,
    setAnchorEl,
  } = useAutocomplete({
    componentName: 'ComboBox',
    options: options,
    filterOptions: filterOptions,
    value: null,
    inputValue: value,
    onInputChange: (event, value, reason) => {
      if (reason === 'selectOption' && value === '' && !options.includes('')) return;

      onChange?.(value, event, reason);
    },
    freeSolo: true,
    open: open,
    disabled: disabled,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    getOptionDisabled: getOptionDisabled,
  });

  const forkedInputRef = useForkRef(rootRef, setAnchorEl);

  const handleClear = (event: unknown) => {
    onChange?.('', event, 'clear');
  };

  const optionsOpen = popupOpen && groupedOptions.length !== 0;

  const rootProps = getRootProps({ onKeyDown, ...rootSlotProps });
  const inputProps = { ...getInputProps(), ...inputSlotProps };

  const [textInputWidth, setTextInputWidth] = useState<number | null>(null);

  useEffect(() => {
    return observeResizeEvent(rootRef, () => {
      setTextInputWidth(rootRef.current?.clientWidth ?? null);
    });
  }, [rootRef]);

  const ownerState: ComboBoxOwnerState = {
    ...props,
    autoWidth,
    textInputWidth,
  };

  return (
    <>
      <ComboBoxTextInput
        ref={forkedInputRef}
        disabled={disabled}
        inputRef={inputRef}
        value={inputProps.value}
        inputProps={inputProps}
        slotProps={{ root: rootProps }}
        ownerState={ownerState}
        onClear={handleClear}
        {...others}
      />
      <ComboBoxPopper
        open={optionsOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              padding: {
                top: COMBO_BOX_GNB_OFFSET,
                right: COMBO_BOX_VIEWPORT_MARGIN,
                bottom: COMBO_BOX_VIEWPORT_MARGIN,
                left: COMBO_BOX_VIEWPORT_MARGIN,
              },
              altBoundary: false,
              altAxis: true,
              tether: true,
            },
          },
          {
            name: 'flip',
            enabled: true,
            options: {
              padding: {
                top: COMBO_BOX_GNB_OFFSET,
                right: COMBO_BOX_VIEWPORT_MARGIN,
                bottom: COMBO_BOX_VIEWPORT_MARGIN,
                left: COMBO_BOX_VIEWPORT_MARGIN,
              },
            },
          },
          ...(popperModifiers ?? []),
        ]}
        ownerState={ownerState}
        ownerZIndex={popperZIndex}
        style={popperStyle}
        {...otherPopperSlotProps}
      >
        <ComboBoxPaper ownerState={ownerState}>
          <ComboBoxMenuList {...getListboxProps()} ownerState={ownerState}>
            {groupedOptions.map((option, optionIndex) => {
              const {
                key,
                'aria-disabled': ariaDisabled,
                ...others
              } = getOptionProps({ option, index: optionIndex });
              const disabled = ariaDisabled === true || ariaDisabled === 'true';

              return (
                <ComboBoxMenuItem
                  key={key}
                  aria-disabled={ariaDisabled}
                  disabled={disabled}
                  {...others}
                >
                  <MenuItemContent
                    text={<TextHighlights originText={option} highlightText={value} />}
                    suffix={renderOptionSuffix?.(option)}
                    disabled={disabled}
                  />
                </ComboBoxMenuItem>
              );
            })}
          </ComboBoxMenuList>
        </ComboBoxPaper>
      </ComboBoxPopper>
    </>
  );
};

const ComboBoxTextInput = styled(TextInput, {
  target: comboBoxClasses.textInput,
})<{ ownerState: ComboBoxOwnerState }>(() => css``);

const ComboBoxPopper = styled(Popper, {
  target: comboBoxClasses.popper,
  shouldForwardProp: prop => prop !== 'ownerState' && prop !== 'ownerZIndex',
})<{ ownerState: ComboBoxOwnerState; ownerZIndex?: number }>`
  display: flex;
  flex-direction: column;
  border-radius: 0px;
  background-color: ${({ theme }) => theme.color['base/white']};
  box-shadow: ${({ theme }) => theme.shadows[2]};
  z-index: ${({ ownerZIndex, theme }) => ownerZIndex ?? theme.zIndex.contentPopup};
`;

const ComboBoxPaper = styled('div', {
  target: comboBoxClasses.paper,
})<{ ownerState: ComboBoxOwnerState }>`
  max-height: min(
    ${COMBO_BOX_MAX_HEIGHT}px,
    calc(100vh - ${COMBO_BOX_GNB_OFFSET}px - ${COMBO_BOX_VIEWPORT_MARGIN * 2}px)
  );
  overflow: auto;

  ${({ ownerState }) => {
    const { autoWidth, textInputWidth: _textInputWidth } = ownerState;
    const textInputWidth = typeof _textInputWidth === 'number' ? _textInputWidth : null;

    if (autoWidth === 'as-content') {
      return '';
    }

    if (autoWidth === 'inside') {
      return textInputWidth ? `max-width: ${textInputWidth}px;` : '';
    }

    if (autoWidth === 'as-input') {
      return textInputWidth ? `width: ${textInputWidth}px;` : '';
    }

    if (autoWidth === 'outside') {
      return textInputWidth ? `min-width: ${textInputWidth}px;` : '';
    }

    return `width: ${autoWidth};`;
  }}
`;

const ComboBoxMenuItem = styled(MenuItem)`
  display: block;
`;

const ComboBoxMenuList = styled(MenuList, {
  target: comboBoxClasses.menuList,
})<{ ownerState: ComboBoxOwnerState }>(() => css``);

export default ComboBox;
